import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useSearchParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { EnvelopeIcon, LockClosedIcon, UserIcon, ArrowRightIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import LanguageSelector from '../../components/LanguageSelector';
import apiService from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface AcceptFormData {
  password: string;
  password_confirm: string;
  name: string;
}

interface PasswordPolicy {
  min_length: number;
  require_uppercase?: boolean;
  require_lowercase?: boolean;
  require_numbers?: boolean;
  require_special_chars?: boolean;
  prohibit_easy_to_guess?: boolean;
  prohibit_sequential_numbers?: boolean;
  prohibit_repeated_characters?: boolean;
  prohibit_common_passwords?: boolean;
  prohibit_user_info?: boolean;
}

interface InviteDetails {
  id: string;
  invitee_email: string;
  company_id: string | null;
  role: string;
  mode: string;
  intent?: string; // 'new_org' | 'new_user' | 'join_existing_user'
  expires_at: string;
  password_policy?: PasswordPolicy;
  company?: { id: string; name: string };
}

const AcceptInvite: React.FC = () => {
  const { t } = useTranslation('acceptInvite');
  const [searchParams] = useSearchParams();
  const rawToken = searchParams.get('token') || '';
  const token = rawToken.trim().replace(/[^a-fA-F0-9]/g, '');
  const navigate = useNavigate();
  const { checkAuthStatus, logout } = useAuth();
  const [invite, setInvite] = useState<InviteDetails | null>(null);
  const [loadingInvite, setLoadingInvite] = useState(!!token);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const minLength = invite?.password_policy?.min_length ?? 8;

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<AcceptFormData>({ defaultValues: { password: '', password_confirm: '', name: '' } });
  const password = watch('password');

  useEffect(() => {
    if (!token) {
      setError(t('errorMissingToken'));
      setLoadingInvite(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await apiService.getInvitationByToken(token);
        if (!cancelled) {
          setInvite(data);
          setError(null);
        }
      } catch (e: any) {
        if (!cancelled) {
          const msg = e.response?.data?.error || e.message || t('errorNotFound');
          setError(msg);
          setInvite(null);
        }
      } finally {
        if (!cancelled) setLoadingInvite(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, t]);

  const onSubmit = async (data: AcceptFormData) => {
    if (!token || !invite) return;
    try {
      setIsSubmitting(true);
      setError(null);
      await apiService.acceptInvitation(token, data.password, data.name?.trim() || undefined);
      toast.success(t('toastSuccess'));
      await checkAuthStatus();
      navigate('/');
    } catch (e: any) {
      const msg = e.response?.data?.error || e.message || t('toastFailed');
      setError(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onAcceptJoin = async () => {
    if (!token || !invite) return;
    try {
      setIsSubmitting(true);
      setError(null);
      await apiService.acceptJoinInvitation(token);
      toast.success('You have successfully joined the company. Please log in to continue.');
      // Force logout so the user re-authenticates and gets a fresh token
      // that reflects the updated company_id, role, and mode.
      await logout();
      navigate(`/login${invite.invitee_email ? `?email=${encodeURIComponent(invite.invitee_email)}` : ''}`);
    } catch (e: any) {
      const msg = e.response?.data?.error || e.message || 'Failed to join company.';
      setError(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isJoinExisting = invite?.intent === 'join_existing_user';
  const policy = invite?.password_policy;
  // Org admin invite (intent new_org): show org-style form with company name. Team invites (intent new_user) show simple form.
  const isCompanyInvite = invite?.intent === 'new_org';
  const labelClass = 'block text-sm font-medium text-gray-300 mb-2';
  const inputClass = 'w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all';
  const iconWrapClass = 'absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500';

  if (loadingInvite) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-black p-4">
        <div className="absolute top-4 right-4">
          <LanguageSelector />
        </div>
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-white/20 border-t-white" />
        <p className="mt-4 text-gray-400">{t('loading')}</p>
      </div>
    );
  }

  if (error && !invite) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-black p-4">
        <div className="absolute top-4 right-4">
          <LanguageSelector />
        </div>
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-8 max-w-md w-full text-center shadow-2xl">
          <p className="text-red-400 mb-4">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4 py-12 relative">
      <div className="absolute top-4 right-4 z-10">
        <LanguageSelector />
      </div>
      <div className={isCompanyInvite ? 'w-full max-w-3xl' : 'w-full max-w-md'}>
        <div className="mb-6">
          <img src="/logo-gov.svg" alt="Governate Logo" className="h-16" />
        </div>
        <h1 className="text-3xl font-bold text-white mb-2">{t('title')}</h1>
        <p className="text-gray-400 mb-6">{t('subtitle', { email: invite?.invitee_email ?? '' })}</p>

        {isJoinExisting ? (
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-8 shadow-2xl space-y-6">
            <div className="flex items-center gap-3">
              <BuildingOfficeIcon className="h-8 w-8 text-white/60 shrink-0" />
              <div>
                <p className="text-white font-semibold text-lg">{invite?.company?.name ?? 'the company'}</p>
                <p className="text-gray-400 text-sm">You have been invited to join this organisation.</p>
              </div>
            </div>
            <p className="text-gray-400 text-sm">
              Your existing account (<span className="text-white">{invite?.invitee_email}</span>) will be connected. No new registration needed.
            </p>
            {error && <p className="text-red-400 text-sm">{error}</p>}
            <button
              type="button"
              onClick={onAcceptJoin}
              disabled={isSubmitting}
              className="w-full py-3 px-4 bg-white text-black font-semibold rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? 'Joining...' : 'Accept & Join'}
              {!isSubmitting && <ArrowRightIcon className="h-4 w-4" />}
            </button>
          </div>
        ) : (

        <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-8 shadow-2xl">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {isCompanyInvite && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
                <div className="space-y-5">
                  <p className="text-sm font-medium text-gray-400 lg:mb-4">{t('form.organization')}</p>
                  <div>
                    <label className={labelClass}>{t('form.companyName')}</label>
                    <div className="relative">
                      <div className={iconWrapClass}>
                        <BuildingOfficeIcon className="h-5 w-5" />
                      </div>
                      <input
                        type="text"
                        value={invite?.company?.name ?? ''}
                        readOnly
                        className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-gray-400 cursor-not-allowed focus:outline-none"
                      />
                    </div>
                  </div>
                </div>
                <div className="space-y-5">
                  <p className="text-sm font-medium text-gray-400 lg:mb-4">{t('form.yourDetails')}</p>
                  <div>
                    <label className={labelClass}>{t('form.email')}</label>
                    <div className="relative">
                      <div className={iconWrapClass}>
                        <EnvelopeIcon className="h-5 w-5" />
                      </div>
                      <input
                        type="email"
                        value={invite?.invitee_email ?? ''}
                        readOnly
                        className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-gray-400 cursor-not-allowed focus:outline-none"
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>{t('form.nameOptional')}</label>
                    <div className="relative">
                      <div className={iconWrapClass}>
                        <UserIcon className="h-5 w-5" />
                      </div>
                      <input
                        type="text"
                        placeholder={t('form.namePlaceholder')}
                        {...register('name')}
                        className={inputClass}
                      />
                    </div>
                  </div>
                  {policy && (
                    <div className="rounded-xl bg-white/5 border border-white/10 p-3 text-sm text-gray-400">
                      <p className="font-medium text-gray-300 mb-2">{t('passwordPolicy.title')}</p>
                      <ul className="list-disc list-inside space-y-0.5">
                        <li>{t('passwordPolicy.minLength', { count: policy.min_length })}</li>
                        {policy.require_uppercase && <li>{t('passwordPolicy.uppercase')}</li>}
                        {policy.require_lowercase && <li>{t('passwordPolicy.lowercase')}</li>}
                        {policy.require_numbers && <li>{t('passwordPolicy.number')}</li>}
                        {policy.require_special_chars && <li>{t('passwordPolicy.specialChar')}</li>}
                        {policy.prohibit_easy_to_guess && <li>{t('passwordPolicy.noEasyGuess')}</li>}
                        {policy.prohibit_sequential_numbers && <li>{t('passwordPolicy.noSequentialNumbers')}</li>}
                        {policy.prohibit_repeated_characters && <li>{t('passwordPolicy.noRepeatedChars')}</li>}
                        {policy.prohibit_common_passwords && <li>{t('passwordPolicy.noCommon')}</li>}
                        {policy.prohibit_user_info && <li>{t('passwordPolicy.noUserInfo')}</li>}
                      </ul>
                    </div>
                  )}
                  <div>
                    <label className={labelClass}>{t('form.password')}</label>
                    <div className="relative">
                      <div className={iconWrapClass}>
                        <LockClosedIcon className="h-5 w-5" />
                      </div>
                      <input
                        type="password"
                        placeholder={t('form.passwordPlaceholder')}
                        {...register('password', {
                          required: t('form.passwordRequired'),
                          minLength: { value: minLength, message: t('form.passwordMinLength', { count: minLength }) },
                        })}
                        className={inputClass}
                      />
                    </div>
                    {errors.password && (
                      <p className="mt-1 text-xs text-red-400">{errors.password.message}</p>
                    )}
                  </div>
                  <div>
                    <label className={labelClass}>{t('form.confirmPassword')}</label>
                    <div className="relative">
                      <div className={iconWrapClass}>
                        <LockClosedIcon className="h-5 w-5" />
                      </div>
                      <input
                        type="password"
                        placeholder={t('form.passwordPlaceholder')}
                        {...register('password_confirm', {
                          required: t('form.confirmRequired'),
                          validate: (v) => v === password || t('form.passwordsNoMatch'),
                        })}
                        className={inputClass}
                      />
                    </div>
                    {errors.password_confirm && (
                      <p className="mt-1 text-xs text-red-400">{errors.password_confirm.message}</p>
                    )}
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-transparent border-2 border-white text-white py-3 px-4 rounded-xl font-medium hover:bg-white hover:text-black focus:outline-none focus:ring-2 focus:ring-white/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center group"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                        {t('submitting')}
                      </>
                    ) : (
                      <>
                        {t('submit')}
                        <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
            {!isCompanyInvite && (
            <>
            <div>
              <label className={labelClass}>{t('form.email')}</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500">
                  <EnvelopeIcon className="h-5 w-5" />
                </div>
                <input
                  type="email"
                  value={invite?.invitee_email ?? ''}
                  readOnly
                  className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-gray-400 cursor-not-allowed focus:outline-none"
                />
              </div>
            </div>
            <div>
              <label className={labelClass}>{t('form.nameOptional')}</label>
              <div className="relative">
                <div className={iconWrapClass}>
                  <UserIcon className="h-5 w-5" />
                </div>
                <input
                  type="text"
                  placeholder={t('form.namePlaceholder')}
                  {...register('name')}
                  className={inputClass}
                />
              </div>
            </div>
            {policy && (
              <div className="rounded-xl bg-white/5 border border-white/10 p-3 text-sm text-gray-400">
                <p className="font-medium text-gray-300 mb-2">{t('passwordPolicy.title')}</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>{t('passwordPolicy.minLength', { count: policy.min_length })}</li>
                  {policy.require_uppercase && <li>{t('passwordPolicy.uppercase')}</li>}
                  {policy.require_lowercase && <li>{t('passwordPolicy.lowercase')}</li>}
                  {policy.require_numbers && <li>{t('passwordPolicy.number')}</li>}
                  {policy.require_special_chars && <li>{t('passwordPolicy.specialChar')}</li>}
                  {policy.prohibit_easy_to_guess && <li>{t('passwordPolicy.noEasyGuess')}</li>}
                  {policy.prohibit_sequential_numbers && <li>{t('passwordPolicy.noSequentialNumbers')}</li>}
                  {policy.prohibit_repeated_characters && <li>{t('passwordPolicy.noRepeatedChars')}</li>}
                  {policy.prohibit_common_passwords && <li>{t('passwordPolicy.noCommon')}</li>}
                  {policy.prohibit_user_info && <li>{t('passwordPolicy.noUserInfo')}</li>}
                </ul>
              </div>
            )}
            <div>
              <label className={labelClass}>{t('form.password')}</label>
              <div className="relative">
                <div className={iconWrapClass}>
                  <LockClosedIcon className="h-5 w-5" />
                </div>
                <input
                  type="password"
                  placeholder={t('form.passwordPlaceholder')}
                  {...register('password', {
                    required: t('form.passwordRequired'),
                    minLength: { value: minLength, message: t('form.passwordMinLength', { count: minLength }) },
                  })}
                  className={inputClass}
                />
              </div>
              {errors.password && (
                <p className="mt-1 text-xs text-red-400">{errors.password.message}</p>
              )}
            </div>
            <div>
              <label className={labelClass}>{t('form.confirmPassword')}</label>
              <div className="relative">
                <div className={iconWrapClass}>
                  <LockClosedIcon className="h-5 w-5" />
                </div>
                <input
                  type="password"
                  placeholder={t('form.passwordPlaceholder')}
                  {...register('password_confirm', {
                    required: t('form.confirmRequired'),
                    validate: (v) => v === password || t('form.passwordsNoMatch'),
                  })}
                  className={inputClass}
                />
              </div>
              {errors.password_confirm && (
                <p className="mt-1 text-xs text-red-400">{errors.password_confirm.message}</p>
              )}
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-transparent border-2 border-white text-white py-3 px-4 rounded-xl font-medium hover:bg-white hover:text-black focus:outline-none focus:ring-2 focus:ring-white/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center group"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                  {t('submitting')}
                </>
              ) : (
                <>
                  {t('submit')}
                  <ArrowRightIcon className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
            </>
            )}
          </form>
        </div>
        )}
      </div>
    </div>
  );
};

export default AcceptInvite;
